import React, { useEffect } from 'react';
import { 
  Box, 
  Container, 
  VStack, 
  Heading, 
  Text, 
  Button, 
  Image, 
  Stat, 
  StatLabel, 
  StatNumber,
  StatGroup,
  Divider,
  Badge,
  Flex,
  useColorModeValue,
  HStack,
  Link
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import ShareResults from './ShareResults';

const ResultScreen = () => {
  const { 
    gameResult, 
    score, 
    resetGame,
    image
  } = useGame();
  
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.700', 'gray.700');
  
  // Redirect to home if no game result
  useEffect(() => {
    if (!gameResult.word && !image) {
      navigate('/');
    }
  }, [gameResult, image, navigate]);
  
  // Handle play again
  const handlePlayAgain = () => {
    resetGame();
    navigate('/');
  };

  return (
    <Container maxW="container.md" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box textAlign="center" mb={2}>
          <Heading 
            as="h1" 
            size="xl" 
            bgGradient="linear(to-r, brand.500, purple.500)" 
            bgClip="text"
          >
            VisualEyes
          </Heading>
          <Text fontSize="lg" color="gray.600">
            Game Results
          </Text>
        </Box>
        
        {/* Result Card */}
        <Box 
          bg={bgColor} 
          borderRadius="lg" 
          boxShadow="xl" 
          overflow="hidden" 
          borderWidth="1px" 
          borderColor={borderColor}
        >
          {/* Result Header */}
          <Box 
            p={6} 
            bg={gameResult.win ? 'green.500' : 'red.500'} 
            color="white"
          >
            <Heading size="lg" textAlign="center">
              {gameResult.win ? 'You got it!' : 'Game Over'}
            </Heading>
          </Box>
          
          {/* Main content */}
          <VStack spacing={6} p={6} align="stretch">
            {/* Word and image */}
            <Flex 
              direction={{ base: 'column', md: 'row' }} 
              gap={6} 
              align="center"
            >
              {/* Word info */}
              <Box flex="1">
                <Text fontSize="lg" fontWeight="bold" mb={2}>
                  The word was:
                </Text>
                <Heading size="xl" color={gameResult.win ? 'green.500' : 'red.500'}>
                  {gameResult.word}
                </Heading>
                
                <Box mt={4}>
                  <Badge colorScheme={gameResult.win ? 'green' : 'red'} fontSize="md" px={2} py={1}>
                    {gameResult.win ? 'Correctly Guessed' : 'Not Guessed'}
                  </Badge>
                </Box>
              </Box>
              
              {/* Image without blur */}
              <Box 
                flex="1" 
                borderRadius="md" 
                overflow="hidden" 
                boxShadow="md" 
                bg="white" 
                p={2}
              >
                {image && (
                  <Image 
                    src={image} 
                    alt={gameResult.word} 
                    borderRadius="md" 
                    width="100%" 
                  />
                )}
              </Box>
            </Flex>
            
            <Divider />
            
            {/* Stats */}
            <StatGroup>
              <Stat>
                <StatLabel>Total Score</StatLabel>
                <StatNumber color="brand.500">{score}</StatNumber>
              </Stat>
              
              <Stat>
                <StatLabel>Status</StatLabel>
                <StatNumber color={gameResult.win ? 'green.500' : 'red.500'}>
                  {gameResult.win ? 'Win' : 'Loss'}
                </StatNumber>
              </Stat>
            </StatGroup>
            
            {/* Share options */}
            <Box>
              <Text fontWeight="bold" mb={2}>
                Share your result:
              </Text>
              <HStack spacing={4} align="center">
                <ShareResults 
                  score={score} 
                  win={gameResult.win} 
                  word={gameResult.word} 
                />
                <Text color="gray.500">or</Text>
                <Link
                  href="https://buymeacoffee.com/themattrix"
                  isExternal
                  _hover={{ textDecoration: 'none' }}
                >
                  <Button
                    size="sm"
                    leftIcon={<span role="img" aria-label="coffee">☕</span>}
                    variant="outline"
                  >
                    Coffee Me
                  </Button>
                </Link>
              </HStack>
            </Box>
          </VStack>
          
          {/* Footer with action buttons */}
          <Box p={6} bg="gray.50" borderTopWidth="1px" borderColor={borderColor}>
            <Button
              colorScheme="brand"
              size="lg"
              width="100%"
              onClick={handlePlayAgain}
              bgGradient="linear(to-r, brand.500, purple.500)"
              _hover={{
                bgGradient: "linear(to-r, brand.600, purple.600)",
              }}
              color="white"
            >
              Play Again
            </Button>
          </Box>
        </Box>
      </VStack>
    </Container>
  );
};

export default ResultScreen;